/**
 * `skills` MCP tool — generic workspace skill-body loader (WP-431 S5, DEC-998).
 *
 * Pure entryId-keyed retrieval: no allow-list, no pb-setup-specific code path.
 * Any caller passing a SKILL-* entryId for their workspace gets the skill body
 * back. The chat-only `start_pb` bootstrap calls this tool internally so the
 * load path stays single-source (S6 contract test pins that).
 *
 * Auth perimeter mirrors `recordTransition` (STD-234): the AKI gateway injects
 * `apiKeyId | personId | clerkUserId` server-side; this tool just calls the
 * gateway via `kernelQuery` and lets the server resolve identity.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { kernelQuery, getWorkspaceId } from "../client.js";

export const skillsSchema = z.object({
  entryId: z
    .string()
    .min(1)
    .describe(
      "Workspace-scoped skill entry id (e.g. 'SKILL-pb-setup'). The tool refuses " +
        "non-skill entries (canonicalKey !== 'skill') with INVALID_KIND.",
    ),
});

export interface SkillBodyResult {
  entryId: string;
  name: string;
  body: string;
  contentType: "text/markdown";
  retrievedAt: number;
}

/**
 * Load a SKILL-* entry's body for the caller's workspace. Internal helper used
 * by both the `skills` tool registration and `start_pb` (which routes through
 * the same loader by contract — DEC-997).
 */
export async function loadSkillBody(entryId: string): Promise<SkillBodyResult> {
  const workspaceId = await getWorkspaceId();
  return await kernelQuery<GatewayRouteReturnByName<"setup.getSkillBody">>("setup.getSkillBody", {
    workspaceId,
    entryId,
  });
}

// WP-484 S3 (clean break, TEN-2157): the standalone `skills` tool retired
// here. loadSkillBody stays live — workflows.ts (the `workflows` compound,
// action=load-skill) and start_pb.ts's chat-only bootstrap are its remaining
// callers.
